import { useTurnstile } from '@/components/anti-bot/useTurnstile';
import ErrorPage from '@/components/error-page';
import { Spinner } from '@/components/ui/spinner';
import { Stepper, type StepperPage } from '@/components/ui/stepper';
import { useForm } from '@/hooks/useForm';
import useFormServerErrors from '@/hooks/useFormServerErrors';
import { type ApiSchema, api, postFormData } from '@/lib/api';
import { focusFirstInvalidField } from '@/lib/form/form-focus';
import { Trans, getKeyFromSelector, useTranslation } from '@/lib/i18n';
import { createFileRoute } from '@tanstack/react-router';
import { useSelector } from '@tanstack/react-store';
import { toMerged } from 'es-toolkit';
import { useState } from 'react';
import { flushSync } from 'react-dom';

import { useShallow } from 'zustand/react/shallow';
import { BoasVindas } from './-boas-vindas';
import { Confirmacao } from './-confirmacao';
import { DadosPrincipais } from './-dados-principais';
import { Documentos } from './-documentos';
import { OutrasInformacoes } from './-outras-informacoes';
import { Responsavel } from './-responsavel';
import {
  type FormData,
  INITIAL_FORM_DATA,
  flattenForApi,
  loadFromStorage,
  mapApiFieldToFormField,
  serializeForStorage,
} from './-schemas';
import { ID_PAIS_BRASIL, STORAGE_KEY } from './-types';

export const Route = createFileRoute('/__public/cadastro-revendedor/')({
  component: RouteComponent,
  errorComponent: ({ reset }) => <ErrorPage reset={reset} />,
});

const LAST_FORM_STEP = 4;
const CONFIRMATION_STEP = 5;

type FormStepperPage = StepperPage<keyof FormData>;
type RevendaErrorCode = ApiSchema<'Revendas.RevendaErrorCode'>;

const revendaMessages: Record<RevendaErrorCode, string> = {
  Required: getKeyFromSelector($ => $.validation.required),
  TooLong: getKeyFromSelector($ => $.validation.invalidField),
  InvalidCnpj: getKeyFromSelector($ => $.validation.invalidCNPJ),
  InvalidEmail: getKeyFromSelector($ => $.validation.invalidField),
  InvalidPhoneNumber: getKeyFromSelector($ => $.validation.invalidPhoneNumber),
  InvalidCellNumber: getKeyFromSelector($ => $.validation.invalidCellNumber),
  InvalidWhatsAppNumber: getKeyFromSelector(
    $ => $.validation.invalidWhatsAppNumber,
  ),
  AtLeastOnePhone: getKeyFromSelector($ => $.validation.atLeastOnePhone),
  InvalidCep: getKeyFromSelector($ => $.validation.invalidCEP),
  AntiBotValidationFailed: getKeyFromSelector(
    $ => $.antiBot.errors.validationFailed,
    { ns: 'public' },
  ),
  FileTooLarge: getKeyFromSelector($ => $.validation.fileTooBig),
  InvalidFileFormat: getKeyFromSelector(
    $ => $.cadastroRevendedor.erros.erroArquivoInvalido,
    { ns: 'public' },
  ),
  ErroDesconhecido: getKeyFromSelector(
    $ => $.cadastroRevendedor.erros.erroEnvio,
    { ns: 'public' },
  ),
};

function RouteComponent() {
  const [step, setStep] = useState(0);
  const [maxVisitedStep, setMaxVisitedStep] = useState(0);
  const [locked, setLocked] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { t } = useTranslation('public');

  const serverErrors = useFormServerErrors(revendaMessages);

  const turnstile = useTurnstile({
    action: 'cadastro-revenda',
    onError: setSubmitError,
  });

  const dadosDoStorage = loadFromStorage();
  const initialValues: FormData = toMerged(INITIAL_FORM_DATA, dadosDoStorage);

  function goToStep(newStep: number) {
    setMaxVisitedStep(prev => Math.max(prev, newStep));
    setStep(newStep);
    setSubmitError(null);
    serverErrors.clear();
  }

  function advanceStep() {
    goToStep(step + 1);
  }

  const form = useForm({
    defaultValues: initialValues,
    async onSubmit({ value, formApi }) {
      serverErrors.clear(formApi);
      setSubmitError(null);

      try {
        const antibot = await turnstile.execute();

        if (!antibot.ok) {
          return;
        }

        const formData = {
          ...flattenForApi(value),
          antiBotToken: antibot.token,
        };

        const { response, error } = await postFormData('/revendas', formData);

        if (error || !response.ok) {
          if (error?.errors.length) {
            serverErrors.apply(
              error.errors.map(e => ({
                ...e,
                field: e.field ? mapApiFieldToFormField(e.field) : e.field,
              })),
              { form: formApi },
            );
          }

          setSubmitError(t($ => $.cadastroRevendedor.erros.erroEnvio));

          return;
        }

        localStorage.removeItem(STORAGE_KEY);

        goToStep(CONFIRMATION_STEP);
        setLocked(true);
      } catch {
        setSubmitError(t($ => $.cadastroRevendedor.erros.erroEnvio));
      }
    },
    listeners: {
      onChange: ({ formApi, fieldApi }) => {
        serverErrors.clear(formApi, [fieldApi.name]);
        setSubmitError(null);
      },
      onBlur: ({ formApi }) => {
        if (!formApi.state.isSubmitSuccessful) {
          localStorage.setItem(
            STORAGE_KEY,
            serializeForStorage(formApi.state.values),
          );
        }
      },
    },
  });

  const [ehBrasil, isSubmitting] = useSelector(
    form.store,
    useShallow(state => [
      state.values.dadosPrincipais.pais === ID_PAIS_BRASIL,
      state.isSubmitting,
    ]),
  );

  const {
    data: dadosIniciais,
    isLoading,
    isError,
  } = api.useQuery('get', '/revendas/dados-iniciais');

  function handleStepClick(targetStep: number) {
    if (targetStep < step) {
      goToStep(targetStep);
      return;
    }

    const groups = Array.from(form.formGroupApis.values());

    const firstInvalidStep = pages
      .slice(0, targetStep)
      .map((page, index) => ({ page, index }))
      .find(({ page }) => {
        const group = groups.find(g => g.name === page.formGroupName);
        return group?.validateSync('submit', {}).hasErrored;
      });

    if (!firstInvalidStep) {
      goToStep(targetStep);
      return;
    }

    flushSync(() => {
      goToStep(firstInvalidStep.index);
    });

    focusFirstInvalidField();
  }

  function handleStepSubmit() {
    advanceStep();
  }

  async function handleFinalSubmit() {
    await form.handleSubmit();
  }

  const paises = [...(dadosIniciais?.paises ?? [])].sort((a, b) => {
    if (a.id === ID_PAIS_BRASIL) return -1;
    if (b.id === ID_PAIS_BRASIL) return 1;
    return 0;
  });

  const estados = dadosIniciais?.estados ?? [];

  const pages: FormStepperPage[] = [
    {
      title: t($ => $.cadastroRevendedor.etapas.boasVindas),
      navigationTitle: t($ => $.cadastroRevendedor.etapas.boasVindasStepper),
      content: <BoasVindas advanceStep={advanceStep} />,
      hideNavigation: true,
    },
    {
      formGroupName: 'dadosPrincipais',
      title: t($ => $.cadastroRevendedor.etapas.dadosPrincipais),
      content: (
        <DadosPrincipais
          form={form}
          onStepSubmit={handleStepSubmit}
          paises={paises}
          estados={estados}
          ehBrasil={ehBrasil}
        />
      ),
    },
    {
      formGroupName: 'documentos',
      title: t($ => $.cadastroRevendedor.etapas.documentos),
      content: (
        <Documentos
          form={form}
          onStepSubmit={handleStepSubmit}
          ehBrasil={ehBrasil}
        />
      ),
    },
    {
      formGroupName: 'contato',
      title: t($ => $.cadastroRevendedor.etapas.responsavel),
      content: <Responsavel form={form} onStepSubmit={handleStepSubmit} />,
    },
    {
      formGroupName: 'outrasInformacoes',
      title: t($ => $.cadastroRevendedor.etapas.outrasInformacoes),
      content: (
        <>
          <OutrasInformacoes form={form} onStepSubmit={handleFinalSubmit} />
          <turnstile.Container className="mt-4" />
        </>
      ),
    },
    {
      title: t($ => $.cadastroRevendedor.etapas.confirmacao),
      content: <Confirmacao />,
      hideNavigation: true,
    },
  ];

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-center text-sm text-destructive">
          {t($ => $.unexpectedError, { ns: 'common' })}
        </p>
      </div>
    );
  }

  return (
    <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 py-10">
      <div className="flex flex-col gap-10">
        <h1 className="text-center text-2xl font-semibold">
          {t($ => $.cadastroRevendedor.tituloPagina)}
        </h1>

        <form.AppForm>
          <Stepper
            pages={pages}
            currentStep={step}
            maxVisitedStep={maxVisitedStep}
            locked={locked}
            onStepClick={handleStepClick}
            navigation={{
              onPrevious: () => {
                goToStep(step - 1);
              },
              isSubmitting,
              isLastFormStep: step === LAST_FORM_STEP,
              submitError: serverErrors.formErrors[0] ?? submitError,
              previousLabel: t($ => $.cadastroRevendedor.navegacao.anterior),
              nextLabel: t($ => $.cadastroRevendedor.navegacao.proximo),
              submitLabel: t($ => $.cadastroRevendedor.navegacao.enviar),
            }}
          />
        </form.AppForm>

        <p className="-mt-6 text-justify text-xs text-muted-foreground">
          <Trans
            ns="public"
            i18nKey={$ => $.cadastroRevendedor.lgpd.informacaoDadosPessoais}
            components={{
              email: (
                <a
                  href="mailto:lgpd@secullum.com.br"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  lgpd@secullum.com.br
                </a>
              ),
            }}
          />
        </p>
      </div>
    </main>
  );
}
